// ONDEMAND-1 (docs/specs/ondemand-1.md): the caller's transcribe-on-demand settings — their
// default policy and their "always transcribe" rules (subscriptions). The nav entry only exists
// when the feature is enabled (App.tsx); a deep link while it's off renders a plain note and
// fires no on-demand request beyond the settings query.
import { useId, useState } from 'react';
import type {
  CreateTranscriptionSubscriptionRequest,
  Folder,
  TranscriptionPolicy,
  TranscriptionSubscription,
  TranscriptionSubscriptionKind,
} from '@scribe/core/types';
import {
  useCreateTranscriptionSubscription,
  useDeleteTranscriptionSubscription,
  useTranscriptionSettings,
  useTranscriptionSubscriptions,
  useUpdateTranscriptionSettings,
  useUpdateTranscriptionSubscription,
} from '../lib/ondemandQueries';
import { useFolders, useTags } from '../lib/queries';
import { Badge } from '../components/ui';

const POLICY_LABEL: Record<TranscriptionPolicy, string> = {
  immediate: 'Transcribe automatically',
  on_access: 'Only when opened, searched or asked about',
};

const KIND_LABEL: Record<TranscriptionSubscriptionKind, string> = {
  all_mine: 'All my recordings',
  folder: 'A folder',
  tag: 'A tag',
  title_match: 'Meeting series title contains…',
  shared_with_me: 'Recordings shared with me',
};

const MIN_TITLE_PATTERN = 3;

function describeRule(sub: TranscriptionSubscription, folders: Map<string, Folder>): string {
  switch (sub.kind) {
    case 'all_mine':
      return 'All my recordings';
    case 'shared_with_me':
      return 'Recordings shared with me';
    case 'folder':
      return `Folder: ${(sub.folder_id && folders.get(sub.folder_id)?.name) ?? 'a deleted folder'}`;
    case 'tag':
      return `Tag: #${sub.tag_name ?? ''}`;
    case 'title_match':
      return `Title contains “${sub.title_pattern ?? ''}”`;
  }
}

export function TranscriptionSettingsScreen() {
  const settings = useTranscriptionSettings();
  const enabled = settings.data?.feature_enabled === true;

  if (!enabled) {
    return (
      <div className="card">
        <div className="card-head">
          <h1>Auto-transcription</h1>
        </div>
        <p className="empty-state">
          {settings.isLoading ? 'Loading…' : 'Every recording is transcribed automatically here.'}
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h1>Auto-transcription</h1>
      </div>
      <p className="muted">
        Recordings are stored right away. Choose which ones are transcribed automatically — the rest
        are transcribed the first time someone opens, searches or asks about them.
      </p>
      <DefaultPolicySection />
      <SubscriptionsSection />
    </div>
  );
}

function DefaultPolicySection() {
  const { data: settings } = useTranscriptionSettings();
  const update = useUpdateTranscriptionSettings();
  const selectId = useId();
  if (!settings) return null;
  const orgDefault = settings.org_default_policy
    ? POLICY_LABEL[settings.org_default_policy]
    : 'unknown';

  return (
    <div className="row-secondary">
      <h2>Your default</h2>
      {settings.webhook_subscriber && (
        <div className="banner banner-info" role="note">
          <p>
            You have a webhook that receives transcripts, so all your recordings are transcribed
            automatically.
          </p>
        </div>
      )}
      <div className="field">
        <label className="label" htmlFor={selectId}>
          New recordings
        </label>
        <select
          id={selectId}
          value={settings.user_default_policy ?? ''}
          disabled={update.isPending}
          onChange={(e) => update.mutate((e.target.value || null) as TranscriptionPolicy | null)}
        >
          <option value="">Follow organization default (currently: {orgDefault})</option>
          <option value="immediate">{POLICY_LABEL.immediate}</option>
          <option value="on_access">{POLICY_LABEL.on_access}</option>
        </select>
      </div>
      {update.isError && <p className="error">{(update.error as Error).message}</p>}
    </div>
  );
}

function SubscriptionsSection() {
  const { data, isLoading, error, refetch } = useTranscriptionSubscriptions(true);
  const { data: folders } = useFolders();
  const updateSub = useUpdateTranscriptionSubscription();
  const deleteSub = useDeleteTranscriptionSubscription();
  const folderById = new Map((folders?.items ?? []).map((f) => [f.id, f]));

  return (
    <div className="row-secondary">
      <h2>Always transcribe</h2>
      <p className="muted">
        Rules apply to new recordings; existing untranscribed recordings transcribe when opened.
      </p>
      {data?.implied_by_webhook && (
        <p className="muted">Your transcript webhook already covers all your recordings.</p>
      )}
      {error && (
        <div className="banner banner-danger">
          <p>{(error as Error).message || 'Failed to load your rules.'}</p>
          <button className="sm" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}
      {isLoading && <p className="muted">Loading rules…</p>}
      {data && data.items.length === 0 && <p className="empty-state">No rules yet.</p>}
      {data && data.items.length > 0 && (
        <ul className="ondemand-sub-list" aria-label="Auto-transcription rules">
          {data.items.map((sub) => {
            const label = describeRule(sub, folderById);
            return (
              <li key={sub.id} className="ondemand-sub">
                <span>
                  {label} {!sub.is_active && <Badge kind="neutral">Paused</Badge>}
                </span>
                <span className="ondemand-sub-actions">
                  <button
                    className="ghost sm"
                    aria-label={`${sub.is_active ? 'Pause' : 'Resume'} rule: ${label}`}
                    disabled={updateSub.isPending}
                    onClick={() => updateSub.mutate({ id: sub.id, isActive: !sub.is_active })}
                  >
                    {sub.is_active ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    className="ghost sm"
                    aria-label={`Delete rule: ${label}`}
                    disabled={deleteSub.isPending}
                    onClick={() => deleteSub.mutate(sub.id)}
                  >
                    Delete
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {updateSub.isError && <p className="error">{(updateSub.error as Error).message}</p>}
      {deleteSub.isError && <p className="error">{(deleteSub.error as Error).message}</p>}
      <NewRuleForm folders={folders?.items ?? []} />
    </div>
  );
}

function NewRuleForm({ folders }: { folders: Folder[] }) {
  const { data: tags } = useTags();
  const create = useCreateTranscriptionSubscription();
  const [kind, setKind] = useState<TranscriptionSubscriptionKind>('all_mine');
  const [folderId, setFolderId] = useState('');
  const [tagName, setTagName] = useState('');
  const [titlePattern, setTitlePattern] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const ids = { kind: useId(), folder: useId(), tag: useId(), title: useId(), tagList: useId() };

  const validation =
    kind === 'folder' && !folderId
      ? 'Pick a folder.'
      : kind === 'tag' && !tagName.trim()
        ? 'Enter a tag name.'
        : kind === 'title_match' && titlePattern.trim().length < MIN_TITLE_PATTERN
          ? `Enter at least ${MIN_TITLE_PATTERN} characters.`
          : null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (validation) {
      setShowErrors(true);
      return;
    }
    const body: CreateTranscriptionSubscriptionRequest = { kind };
    if (kind === 'folder') body.folder_id = folderId;
    if (kind === 'tag') body.tag_name = tagName.trim().replace(/^#/, '');
    if (kind === 'title_match') body.title_pattern = titlePattern.trim();
    create.mutate(body, {
      onSuccess: () => {
        setShowErrors(false);
        setFolderId('');
        setTagName('');
        setTitlePattern('');
      },
    });
  }

  return (
    <form onSubmit={submit} aria-label="New auto-transcription rule">
      <h3 className="label" style={{ marginBottom: 8 }}>
        Add a rule
      </h3>
      <div className="field">
        <label className="label" htmlFor={ids.kind}>
          Always transcribe
        </label>
        <select
          id={ids.kind}
          value={kind}
          onChange={(e) => {
            setKind(e.target.value as TranscriptionSubscriptionKind);
            setShowErrors(false);
          }}
        >
          {(Object.keys(KIND_LABEL) as TranscriptionSubscriptionKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
      </div>
      {kind === 'folder' && (
        <div className="field">
          <label className="label" htmlFor={ids.folder}>
            Folder
          </label>
          <select id={ids.folder} value={folderId} onChange={(e) => setFolderId(e.target.value)}>
            <option value="">Choose a folder…</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {kind === 'tag' && (
        <div className="field">
          <label className="label" htmlFor={ids.tag}>
            Tag name
          </label>
          <input
            id={ids.tag}
            list={ids.tagList}
            value={tagName}
            placeholder="e.g. client-call"
            onChange={(e) => setTagName(e.target.value)}
          />
          <datalist id={ids.tagList}>
            {tags?.items.map((t) => (
              <option key={t.id} value={t.name} />
            ))}
          </datalist>
        </div>
      )}
      {kind === 'title_match' && (
        <div className="field">
          <label className="label" htmlFor={ids.title}>
            Title contains
          </label>
          <input
            id={ids.title}
            value={titlePattern}
            placeholder="e.g. Weekly planning"
            onChange={(e) => setTitlePattern(e.target.value)}
          />
        </div>
      )}
      {showErrors && validation && (
        <p className="error" role="alert">
          {validation}
        </p>
      )}
      {create.isError && <p className="error">{(create.error as Error).message}</p>}
      <div className="row">
        <button disabled={create.isPending}>{create.isPending ? 'Adding…' : 'Add rule'}</button>
      </div>
    </form>
  );
}
